import { useEffect, useRef } from 'react';

import { pushTracking } from '@src/utils';
import { Event, Params } from '@src/type';

import logEvent from '../../../../src/utils/datadogRum';
import {
  trackVideoStartedEvent,
  trackVideoResumed,
  trackVideoPausedEvent,
  trackVideoBufferStartedEvent,
  trackVideoBufferCompletedEvent,
  trackVideoSeekStartedEvent,
  trackVideoSeekCompletedEvent,
  trackVideoContentPlaying,
  trackVideoCompletedEvent,
} from '../utils/segment/segment';

/**
 * Owns the identity and lifecycle of a single workout video session on the Mux
 * player. A session is one play attempt: it begins when playback first starts
 * (a fresh per-play id is minted), and ends on completion, an inactivity
 * timeout, or unmount. Pauses/seeks stay in the same session; a replay mints a
 * new id. See Confluence "LM+ Frontend Session Management" for the full model.
 *
 * The handlers are player-agnostic (each takes a numeric playback position), so
 * the Mux player adapter just forwards `event.target.currentTime`.
 */

// 30 minutes of inactivity closes the session so the next play is a new one.
// Tunable; matches the Adobe Media default (Mux uses 60).
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const HEARTBEAT_INTERVAL_SECONDS = 60;
const PROGRESS_MILESTONES = [10, 25, 50, 75, 80, 90];

export interface UseVideoSessionProps {
  videoId?: string;
  contentAssetId?: string;
  duration?: number;
  workoutName: string;
  planId?: string | null;
}

const generateSessionId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const useVideoSession = ({
  videoId,
  contentAssetId,
  duration,
  workoutName,
  planId,
}: UseVideoSessionProps) => {
  // Lifecycle state lives in refs so it survives re-renders without causing them.
  const sessionIdRef = useRef<string>('');
  const hasStartedRef = useRef<boolean>(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const milestonesReachedRef = useRef<{ [percentage: number]: boolean }>({});

  const totalLength = duration ?? 0;

  const buildPayload = (position: number) => ({
    platform: 'web',
    content_asset_id: contentAssetId ?? '',
    total_length: totalLength,
    position,
    session_id: sessionIdRef.current,
    title: workoutName,
  });

  const percentage = (position: number) =>
    totalLength > 0 ? parseInt(`${(position / totalLength) * 100}`, 10) : 0;

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  // Reset on every player event. While playing, `onTimeUpdate` keeps it alive;
  // while paused/idle it fires after the window and closes the session, so the
  // next `onPlay` mints a fresh id and the bout is recorded separately.
  const resetIdleTimer = () => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      hasStartedRef.current = false;
    }, INACTIVITY_TIMEOUT_MS);
  };

  // HTML5 `play` intent: initial play and unpause-from-pause. The `hasStarted`
  // flag — not `position === 0` — distinguishes a new session from a resume, so
  // seeking to the start (which does not re-fire `play`) cannot mint a new id.
  const onPlay = (position = 0) => {
    resetIdleTimer();

    if (!hasStartedRef.current) {
      sessionIdRef.current = generateSessionId();
      hasStartedRef.current = true;
      milestonesReachedRef.current = {};

      logEvent('start_video', {
        content_name: workoutName,
        video_vimeo_id: videoId,
        ...(planId && { plan_id: planId }),
      });
      pushTracking({
        event: Event.VIDEO_PLAY,
        [Params.VIDEO_TITLE]: workoutName,
        [Params.VIDEO_DURATION]: duration,
        [Params.VIDEO_PLAY_PERCENTAGE]: 0,
        [Params.VIDEO_VIMEO_ID]: videoId,
      });
      trackVideoStartedEvent(buildPayload(position));
      return;
    }

    logEvent('resume_video', {
      content_name: workoutName,
      video_current_time: position,
      video_duration: duration,
      video_vimeo_id: videoId,
    });
    pushTracking({
      event: Event.VIDEO_UNPAUSE,
      [Params.VIDEO_TITLE]: workoutName,
      [Params.VIDEO_PLAY_PERCENTAGE]: percentage(position),
      [Params.VIDEO_VIMEO_ID]: videoId,
      [Params.VIDEO_CURRENT_TIME]: position,
      [Params.VIDEO_DURATION]: duration,
    });
    trackVideoResumed(buildPayload(position));
  };

  // Playback running after a stall/start: pairs with `onWaiting` to exclude
  // rebuffer time. Does not touch session identity.
  const onPlaying = (position = 0) => {
    resetIdleTimer();
    trackVideoBufferCompletedEvent(buildPayload(position));
  };

  const onWaiting = (position = 0) => {
    resetIdleTimer();
    trackVideoBufferStartedEvent(buildPayload(position));
  };

  const onPause = (position = 0) => {
    resetIdleTimer();
    trackVideoPausedEvent(buildPayload(position));
  };

  const onSeeking = (position = 0) => {
    resetIdleTimer();
    trackVideoSeekStartedEvent(buildPayload(position));
  };

  const onSeeked = (position = 0) => {
    resetIdleTimer();
    trackVideoSeekCompletedEvent(buildPayload(position));
  };

  const onTimeUpdate = (position = 0) => {
    resetIdleTimer();

    if (position > 0 && Math.round(position) % HEARTBEAT_INTERVAL_SECONDS === 0) {
      trackVideoContentPlaying({ ...buildPayload(position), asset_id: contentAssetId ?? '' });
    }

    const pct = percentage(position);
    if (PROGRESS_MILESTONES.includes(pct) && !milestonesReachedRef.current[pct]) {
      milestonesReachedRef.current[pct] = true;
      logEvent('progress_video', {
        content_name: workoutName,
        video_current_time: position,
        video_duration: duration,
        video_play_percent: pct,
        video_vimeo_id: videoId,
      });
      pushTracking({
        event: Event.VIDEO_PROGRESS,
        [Params.VIDEO_TITLE]: workoutName,
        [Params.VIDEO_PLAY_PERCENTAGE]: pct,
        [Params.VIDEO_VIMEO_ID]: videoId,
        [Params.VIDEO_CURRENT_TIME]: position,
        [Params.VIDEO_DURATION]: duration,
      });
    }
  };

  // Completion closes the session; a replay (next `onPlay`) mints a new id.
  const onEnded = (position = 0) => {
    clearIdleTimer();
    hasStartedRef.current = false;
    trackVideoCompletedEvent(buildPayload(position));
  };

  useEffect(() => clearIdleTimer, []);

  return {
    onPlay,
    onPlaying,
    onWaiting,
    onPause,
    onSeeking,
    onSeeked,
    onTimeUpdate,
    onEnded,
    getSessionId: () => sessionIdRef.current,
  };
};

export default useVideoSession;
